import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../config';
import {
  deleteMe,
  deleteUser,
  editMe,
  editUser,
  getMe,
  getUser,
  getUsers,
} from './methods';
import { EditUserRequest } from './types';

const useGetMe = () => {
  const { isPending, data, error } = useQuery({
    queryFn: () => getMe(),
    queryKey: [queryKeys.meKey],
    retry: false,
  });

  return { data, error, isPending };
};

const useGetUserQuery = (id: number) => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    enabled: !!id,
    queryFn: () => getUser(id),
    queryKey: [queryKeys.userKey, id],
  });

  return { data, isError, isLoading, isSuccess };
};

const useGetUsersQuery = () => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    queryFn: getUsers,
    queryKey: [queryKeys.userKey],
    staleTime: Infinity,
  });

  return { data, isError, isLoading, isSuccess };
};

const useEditMeMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editMeMutation,
  } = useMutation({
    mutationFn: editMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.userKey] });
    },
  });

  return { editMeMutation, isError, isPending, isSuccess };
};

const useEditUserMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editUserMutation,
  } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditUserRequest }) =>
      editUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.userKey] });
    },
  });

  return { editUserMutation, isError, isPending, isSuccess };
};

const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteUserMutation,
  } = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.userKey] });
    },
  });

  return { deleteUserMutation, isError, isPending, isSuccess };
};

const useDeleteMeMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteMeMutation,
  } = useMutation({
    mutationFn: deleteMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.userKey] });
    },
  });

  return { deleteMeMutation, isError, isPending, isSuccess };
};

export {
  useDeleteMeMutation,
  useDeleteUserMutation,
  useEditMeMutation,
  useEditUserMutation,
  useGetMe,
  useGetUserQuery,
  useGetUsersQuery,
};
